import React from 'react';
import { Globe, Award, Lightbulb, Users } from 'lucide-react';
import { aboutContent } from '../mockData';

const About = () => {
  const iconMap = {
    Globe: Globe,
    Award: Award,
    Lightbulb: Lightbulb,
    Users: Users
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              About Fresh Bites Group
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Bridging tradition and innovation in food manufacturing
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {aboutContent.mission}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Vision</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {aboutContent.vision}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {aboutContent.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`Traditional cooking ${index + 1}`}
                  className="w-full h-64 object-cover rounded-none shadow-lg hover:shadow-xl transition-all duration-300"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
              Our Story
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {aboutContent.story}
            </p>
            <div className="bg-white p-8 rounded-none shadow-lg border-l-4 border-emerald-600">
              <p className="text-lg text-gray-800 italic">
                "We believe in preserving the authentic flavors and nutritional value of traditional recipes 
                while making them accessible to modern consumers through scalable, compliant manufacturing processes."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.values.map((value, index) => {
              const Icon = iconMap[value.icon];
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-blue-600"
                >
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <Icon size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{value.title}</h3>
                  <p className="text-gray-600 text-center">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Facilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              State-of-the-art production facilities in Birmingham
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-none shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Production Facility</h3>
              <p className="text-gray-700 mb-4">
                Kitchen 4, Griller Kitchens<br />
                121 Miller St<br />
                Birmingham B6 4NG<br />
                United Kingdom
              </p>
              <p className="text-gray-600">
                Our HACCP-certified production facility features modern equipment for recipe development, 
                pilot production, and full-scale manufacturing operations.
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Head Office</h3>
              <p className="text-gray-700 mb-4">
                706 Canterbury House<br />
                Birmingham B31DB<br />
                United Kingdom
              </p>
              <p className="text-gray-600">
                Our head office coordinates business development, quality assurance, 
                and partner relationships across the UK market.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Fresh Bites Group Ltd
          </h2>
          <p className="text-xl text-emerald-100 mb-4">
            Registered in England and Wales
          </p>
          <p className="text-lg text-emerald-200">
            Company No: 15757659
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
